/**
 * ForwardModal
 *
 * Modal to forward a message to one or more conversations.
 * Shows conversation list with search, allows multi-select, then forwards.
 */

import { useState, useMemo } from 'react';
import { Modal, Avatar } from '@/components/ui';
import { useAuth } from '@/hooks';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import ConversationService from '@/services/messaging/ConversationService';

export default function ForwardModal({ open, onClose, message, conversations = [] }) {
  const { user } = useAuth();
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [forwarding, setForwarding] = useState(false);

  const filtered = useMemo(() => {
    if (!search) return conversations;
    const q = search.toLowerCase();
    return conversations.filter(c =>
      c.subject?.toLowerCase().includes(q) ||
      c.participants?.some(p => p.name?.toLowerCase().includes(q))
    );
  }, [conversations, search]);

  const toggleSelect = (convId) => {
    setSelected(prev =>
      prev.includes(convId)
        ? prev.filter(id => id !== convId)
        : [...prev, convId]
    );
  };

  const handleForward = async () => {
    if (!message || selected.length === 0 || !user?.uid) return;
    setForwarding(true);

    try {
      await Promise.all(selected.map(convId =>
        ConversationService.sendMessage(convId, {
          senderId: user.uid,
          senderName: user.displayName || user.email || 'Unknown',
          senderRole: 'teacher',
          text: message.text || '',
          media: message.media || null,
          type: message.type || 'text',
          forwardedFrom: {
            conversationId: message.conversationId,
            messageId: message.id,
            senderName: message.senderName
          }
        })
      ));

      setSelected([]);
      setSearch('');
      onClose();
    } catch (err) {
      console.error('Error forwarding message:', err);
    } finally {
      setForwarding(false);
    }
  };

  const getDisplayName = (conv) => {
    const others = conv.participants?.filter(p => p.userId !== user?.uid) || [];
    if (conv.type === 'direct' && others.length === 1) return others[0].name;
    return conv.subject || conv.groupName || 'Conversation';
  };

  return (
    <Modal open={open} onClose={onClose} title="Forward message" size="sm">
      <div className="space-y-4">
        {/* Search */}
        <div className="relative">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
          <input
            type="search"
            placeholder="Search conversations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input input-bordered input-sm w-full pl-9"
            autoFocus
          />
        </div>

        {/* Forwarded message preview */}
        {message && (
          <div className="bg-base-200 rounded-lg p-3 text-sm">
            <span className="text-xs text-base-content/50 font-medium">{message.senderName}</span>
            <p className="truncate mt-0.5">{message.text || '[Media]'}</p>
          </div>
        )}

        {/* Conversation list */}
        <div className="max-h-64 overflow-y-auto -mx-1">
          {filtered.length === 0 ? (
            <p className="text-sm text-base-content/50 text-center py-4">No conversations found</p>
          ) : (
            filtered.map(conv => {
              const isSelected = selected.includes(conv.id);
              const others = conv.participants?.filter(p => p.userId !== user?.uid) || [];

              return (
                <label
                  key={conv.id}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-base-200 transition-colors ${
                    isSelected ? 'bg-primary/5' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm checkbox-primary"
                    checked={isSelected}
                    onChange={() => toggleSelect(conv.id)}
                  />
                  {others.length <= 1 ? (
                    <Avatar name={others[0]?.name || '?'} size="xs" />
                  ) : (
                    <div className="avatar-group -space-x-2">
                      {others.slice(0, 2).map(p => (
                        <Avatar key={p.userId} name={p.name} size="xs" />
                      ))}
                    </div>
                  )}
                  <span className="text-sm truncate flex-1">{getDisplayName(conv)}</span>
                </label>
              );
            })
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleForward}
            disabled={selected.length === 0 || forwarding}
          >
            {forwarding ? 'Forwarding...' : `Forward${selected.length > 0 ? ` (${selected.length})` : ''}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
